/**
 * DOCUMENTATION:
 * Component: DesignPatternsCreational
 * Purpose: Educational page teaching the Singleton and Factory design patterns.
 * * * Note for Junior Developers:
 * - Creational patterns are all about "Who creates the object and when?"
 * - Singleton: "There can be only one." (Global state, loggers, DB connections).
 * - Factory: "I don't care how it's made, just give me the right one." (UI components, API handlers).
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Fab, Zoom, Tooltip, Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1508921234172-b68ed335b3e6";

/* ✅ Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
});

/* ✅ Main Component */
export default function DesignPatternsCreational() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const patterns = useMemo(() => [
        {
            name: "Singleton",
            intent: "Ensure a class has only one instance and provide a global point of access to it.",
            useCase: "Database connections, Redux/State Stores, Logger services."
        },
        {
            name: "Factory Method",
            intent: "Define an interface for creating an object, but let subclasses decide which class to instantiate.",
            useCase: "Cross-platform UI components (rendering a different button for iOS vs Android)."
        }
    ], []);

    const codingModules = useMemo(() => [
        {
            title: "1. The Singleton Pattern (Vanilla JS)",
            code: `// ✅ Ensuring only one Database instance exists\nclass Database {\n  constructor() {\n    if (Database.instance) {\n      return Database.instance;\n    }\n    this.connection = "Connected to DB";\n    Database.instance = this;\n  }\n\n  query(sql) {\n    console.log(\`Executing: \${sql}\`);\n  }\n}\n\nconst db1 = new Database();\nconst db2 = new Database();\n\nconsole.log(db1 === db2); // true — Both point to the exact same object!`,
            desc: "The Singleton is controversial because it creates global state. However, for things like a Telemetry Logger or a Configuration Manager, it prevents your app from opening 100 unnecessary connections."
        },
        {
            title: "2. The Factory Pattern (UI Component Context)",
            code: `// ✅ A Factory that generates different UI components based on type\nconst ButtonFactory = (type, props) => {\n  switch(type) {\n    case 'primary':\n      return <button className="btn-gold" {...props} />;\n    case 'danger':\n      return <button className="btn-red" {...props} />;\n    case 'ghost':\n      return <button className="btn-outline" {...props} />;\n    default:\n      return <button className="btn-standard" {...props} />;\n  }\n};\n\n// Usage in a component:\nconst MyAction = () => (\n  <div>\n    {ButtonFactory('primary', { children: 'Submit' })}\n  </div>\n);`,
            desc: "Factories decouple the *creation* logic from the *rendering* logic. If you ever need to change how 'danger' buttons look across the entire app, you only update the Factory."
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    CREATIONAL PATTERNS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Abstracting Instantiation
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Creational patterns provide various object creation mechanisms, which increase flexibility and reuse of existing code. Instead of hardcoding <code>new Class()</code> inside your business logic, you delegate that responsibility to a pattern.
                        </Typography>



                        [Image of the Singleton Pattern UML diagram]


                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}><AiFillBulb size={24} /></Avatar>
                            <Typography variant="h6" fontWeight={700}>Core Creational Patterns</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            {patterns.map((item, i) => (
                                <Grid item xs={12} md={6} key={i}>
                                    <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", height: "100%", border: "1px solid rgba(218, 165, 32, 0.2)" }}>
                                        <Typography variant="subtitle1" sx={{ color: "goldenrod", fontWeight: 800 }}>{item.name}</Typography>
                                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>{item.intent}</Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>USE CASE: {item.useCase}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>



                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button size="small" variant="outlined" onClick={() => handleCopy(item.code, i)} sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button size="small" variant="outlined" startIcon={<CodeIcon />} sx={{ color: "goldenrod", borderColor: "goldenrod" }}>
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center">
                    <Link to="/design-patterns-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ backgroundColor: "goldenrod", color: "#100720", fontWeight: 800, px: 4, borderRadius: "999px", "&:hover": { backgroundColor: "#e0b200" } }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
